import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';

import {
  DeleteVendorTypeResult,
  UpsertVendorTypeRequest,
  VendorType,
  VendorTypeApiItem,
} from './vendor-type.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class VendorTypeService {
  private readonly endpoint = apiUrl('VendorType');

  private activeVendorTypes$?: Observable<VendorType[]>;

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService,
  ) {}

  getVendorTypes(activeOnly = false): Observable<VendorType[]> {
    const params = activeOnly ? new HttpParams().set('activeOnly', 'true') : undefined;

    return this.http.get<unknown>(this.endpoint, { params }).pipe(
      map((response) => this.extractRows(response).map((item) => this.normalizeVendorType(item))),
      map((rows) => rows.sort((left, right) => left.name.localeCompare(right.name))),
    );
  }

  /** Cached picker feed shared by the vendor forms; invalidated whenever the catalog changes. */
  getActiveVendorTypeOptions(): Observable<VendorType[]> {
    if (!this.activeVendorTypes$) {
      this.activeVendorTypes$ = this.getVendorTypes(true).pipe(
        catchError(() => of<VendorType[]>([])),
        shareReplay(1),
      );
    }

    return this.activeVendorTypes$;
  }

  invalidateVendorTypeOptions(): void {
    this.activeVendorTypes$ = undefined;
  }

  createVendorType(payload: UpsertVendorTypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, {
      ...payload,
      createdBy: this.resolveCurrentUser(),
    });
  }

  updateVendorType(vendorTypeId: number, payload: UpsertVendorTypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${vendorTypeId}`, {
      ...payload,
      modifiedBy: this.resolveCurrentUser(),
    });
  }

  deleteVendorType(vendorTypeId: number): Observable<DeleteVendorTypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${vendorTypeId}`).pipe(
      map((): DeleteVendorTypeResult => ({ kind: 'success' })),
      catchError((error: HttpErrorResponse) => of<DeleteVendorTypeResult>({
        kind: 'error',
        message: error.status === 409
          ? 'This vendor type is still assigned to one or more vendors and cannot be removed.'
          : 'Unable to delete vendor type at this time. Please try again.',
      })),
    );
  }

  private normalizeVendorType(item: VendorTypeApiItem): VendorType {
    return {
      id: this.toNumber(item.id),
      code: this.toText(item.code),
      name: this.toText(item.name),
      description: this.toText(item.description),
      isActive: item.isActive !== false,
      vendorCount: this.toNumber(item.vendorCount),
    };
  }

  private extractRows(response: unknown): VendorTypeApiItem[] {
    if (Array.isArray(response)) {
      return response as VendorTypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as VendorTypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as VendorTypeApiItem[];
      }
    }

    return [];
  }

  private resolveCurrentUser(): string {
    const active = this.msalService.instance.getActiveAccount();
    if (active?.username) {
      return active.username;
    }

    const first = this.msalService.instance.getAllAccounts()[0];
    return first?.username ?? 'unknown.user';
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
